import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories } from './articles';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const BlogJsonLd = () => (
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/blog#webpage`,
          "url": `${SITE_URL}/blog`,
          "name": "Electronics Recycling Blog & Resources | E-Waste Tips Los Angeles | Greenly",
          "description": "E-waste recycling guides and tips for Los Angeles — how to recycle TVs, computers, batteries, and organize community events. CalRecycle certified CEWD-127531.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "about": { "@id": `${SITE_URL}/#business` },
          "breadcrumb": { "@id": `${SITE_URL}/blog#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${SITE_URL}/blog#breadcrumb`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Blog & Resources", "item": `${SITE_URL}/blog` }
          ]
        },
        {
          "@type": "Blog",
          "@id": `${SITE_URL}/blog#blog`,
          "url": `${SITE_URL}/blog`,
          "name": "Greenly Electronics Recycling Blog & Resources",
          "description": "E-waste recycling tips, guides, and resources for Los Angeles residents and businesses. Learn how to recycle TVs, computers, batteries, and more responsibly.",
          "publisher": { "@id": `${SITE_URL}/#business` },
          "blogPost": articles.map((article) => ({
            "@type": "BlogPosting",
            "headline": article.title,
            "url": `${SITE_URL}/blog/${article.slug}`,
            "datePublished": article.date,
            "author": { "@type": "Organization", "name": "Greenly Electronics Recycling" },
            "image": article.image,
            "description": article.excerpt,
          })),
        }
      ]
    })}
  </script>
);

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const filteredArticles = activeCategory === 'All'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  return (
    <>
      <title>Electronics Recycling Blog &amp; Resources | E-Waste Tips Los Angeles | Greenly</title>
      <meta name="description" content="E-waste recycling guides and tips for Los Angeles — how to recycle TVs, computers, batteries, and organize community events. CalRecycle certified CEWD-127531." />
      <meta name="keywords" content="e-waste recycling tips, electronics recycling blog Los Angeles, how to recycle old TV, battery recycling guide, hard drive destruction guide" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/blog`} />

      {/* Open Graph / Social Share Meta Tags */}
      <meta property="og:title" content="Electronics Recycling Blog & Resources | E-Waste Tips Los Angeles | Greenly" />
      <meta property="og:description" content="E-waste recycling guides and tips for Los Angeles — how to recycle TVs, computers, batteries, and organize community events. CalRecycle certified CEWD-127531." />
      <meta property="og:image" content="/images/greenly.png" />
      <meta property="og:url" content={`${SITE_URL}/blog`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Greenly Electronics Recycling" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Electronics Recycling Blog & Resources | E-Waste Tips Los Angeles | Greenly" />
      <meta name="twitter:description" content="E-waste recycling guides and tips for Los Angeles — how to recycle TVs, computers, batteries, and organize community events. CalRecycle certified CEWD-127531." />
      <meta name="twitter:image" content="/images/greenly.png" />
      <meta name="twitter:site" content="@greenlyrecycling" />

      <BlogJsonLd />
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="bg-[#0F3D22] pt-28 pb-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: 'Blog & Resources' }]} />
            <h1 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-3">
              Electronics Recycling Blog &amp; Resources
            </h1>
            <p className="text-green-200 max-w-2xl text-base leading-relaxed">
              Expert guides on e-waste recycling in Los Angeles — from TV and computer disposal to battery safety and community event planning. CalRecycle certified.
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-[#F4FAF7] border-b border-green-100 py-5 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-[#1B6B3A] text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-[#1B6B3A] hover:text-[#1B6B3A]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filteredArticles.map((article) => (
                <article
                  key={article.slug}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#1B6B3A]/30 hover:shadow-sm transition-all duration-200 group"
                >
                  <Link to={`/blog/${article.slug}`} className="block">
                    <div className="w-full h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        width={400}
                        height={208}
                      />
                    </div>
                  </Link>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold text-[#1B6B3A] bg-[#E8F5EE] px-2.5 py-1 rounded-full">
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-400">{article.readTime}</span>
                    </div>
                    <Link to={`/blog/${article.slug}`} className="block">
                      <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1B6B3A] transition-colors leading-snug">
                        {article.title}
                      </h2>
                    </Link>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{article.date}</span>
                      <Link
                        to={`/blog/${article.slug}`}
                        className="text-sm font-semibold text-[#1B6B3A] hover:text-[#2D8653] transition-colors inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
                      >
                        Read More
                        <i className="ri-arrow-right-line text-xs"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-[#E8F5EE] rounded-full mx-auto mb-4">
                  <i className="ri-article-line text-[#1B6B3A] text-2xl"></i>
                </div>
                <p className="text-gray-500 text-lg">No articles found in this category.</p>
                <button
                  onClick={() => setActiveCategory('All')}
                  className="mt-3 text-[#1B6B3A] font-semibold text-sm cursor-pointer hover:underline"
                >
                  View all articles
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#F4FAF7] py-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Have E-Waste to Recycle?
            </h2>
            <p className="text-gray-500 mb-8">
              Free residential drop-off and business pickup throughout Los Angeles County. Certified, responsible, and convenient.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/dropoff-form"
                className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-2-line"></i>
                Free Drop-Off
              </Link>
              <Link
                to="/pickup-request"
                className="inline-flex items-center gap-2 border-2 border-[#1B6B3A] text-[#1B6B3A] font-semibold px-7 py-3.5 rounded-full hover:bg-[#E8F5EE] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-truck-line"></i>
                Schedule Pickup
              </Link>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="py-14 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Learn More</div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">More Resources</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/e-waste-guide"
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-lg group-hover:bg-[#1B6B3A] transition-colors">
                    <i className="ri-book-open-line text-[#1B6B3A] text-sm group-hover:text-white transition-colors"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#1B6B3A] transition-colors">E-Waste Guide & Glossary</h3>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">Learn what e-waste is, the 10 types, California laws, and how to recycle responsibly.</p>
              </Link>
              <Link
                to="/sustainability-report"
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-lg group-hover:bg-[#1B6B3A] transition-colors">
                    <i className="ri-bar-chart-box-line text-[#1B6B3A] text-sm group-hover:text-white transition-colors"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#1B6B3A] transition-colors">Business Sustainability</h3>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">Corporate e-waste recycling programs, ESG documentation, and Certificates of Destruction.</p>
              </Link>
              <Link
                to="/faq"
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-lg group-hover:bg-[#1B6B3A] transition-colors">
                    <i className="ri-question-answer-line text-[#1B6B3A] text-sm group-hover:text-white transition-colors"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#1B6B3A] transition-colors">Items We Recycle & FAQ</h3>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">Full list of accepted items and answers to 20+ common e-waste questions.</p>
              </Link>
              <Link
                to="/events"
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-lg group-hover:bg-[#1B6B3A] transition-colors">
                    <i className="ri-calendar-event-line text-[#1B6B3A] text-sm group-hover:text-white transition-colors"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#1B6B3A] transition-colors">E-Waste Events</h3>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">Community e-waste collection events across Los Angeles County.</p>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BlogPage;